import { LOCAL_STORAGE_KEYS, REMOVE_TOKENS } from "../common/constants";
import { parseJWTToken, filterKeys } from "../common/utils";
import { googleLogout } from "@react-oauth/google";
import { fetchUserData } from "../services/auth.service";

export class Auth {
  private static instance: Auth;
  authenticated: boolean;
  isRefreshing: boolean;
  refreshSubscribers: never[];
  accessToken: string | null;
  refreshToken: string | null;
  googleAuthToken: string | null;
  googleAccessToken: string | null;
  googleRefreshToken: string | null;
  userMetaData: {};

  requiredDataKeys = [
    "email",
    "family_name",
    "given_name",
    "picture",
    "username",
  ];

  constructor() {
    this.authenticated = false;
    this.isRefreshing = false;
    this.refreshSubscribers = [];
    this.accessToken = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
    this.refreshToken = localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
    this.googleAccessToken = localStorage.getItem(
      LOCAL_STORAGE_KEYS.GOOGLE_ACCESS_TOKEN
    );
    this.googleRefreshToken = localStorage.getItem(
      LOCAL_STORAGE_KEYS.GOOGLE_REFRESH_TOKEN
    );
    this.googleAuthToken = localStorage.getItem(
      LOCAL_STORAGE_KEYS.GOOGLE_AUTH_TOKEN
    );
    this.userMetaData = JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_KEYS.USER_META_DATA) || "{}"
    );
  }

  public static getInstance(): Auth {
    return this.instance || (this.instance = new this());
  }

  getAccessToken() {
    return (
      this.accessToken || localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN)
    );
  }

  getRefreshToken() {
    return (
      this.refreshToken ||
      localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN)
    );
  }

  getGoogleAccessToken() {
    return (
      this.googleAccessToken ||
      localStorage.getItem(LOCAL_STORAGE_KEYS.GOOGLE_ACCESS_TOKEN)
    );
  }

  getGoogleRefreshToken() {
    return (
      this.googleRefreshToken ||
      localStorage.getItem(LOCAL_STORAGE_KEYS.GOOGLE_REFRESH_TOKEN)
    );
  }

  getUserMetaData() {
    return (
      this.userMetaData ||
      JSON.parse(
        localStorage.getItem(LOCAL_STORAGE_KEYS.USER_META_DATA) || "{}"
      )
    );
  }

  saveAccessToken(token: string) {
    this.accessToken = token;
    localStorage.setItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, token);
  }

  saveRefreshToken(token: string) {
    this.refreshToken = token;
    localStorage.setItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN, token);
  }

  saveGoogleAuthToken(token: string) {
    this.googleAuthToken = token;
    localStorage.setItem(LOCAL_STORAGE_KEYS.GOOGLE_AUTH_TOKEN, token);
  }

  saveGoogleAccessToken(token: string) {
    this.googleAccessToken = token;
    localStorage.setItem(LOCAL_STORAGE_KEYS.GOOGLE_ACCESS_TOKEN, token);
  }

  saveGoogleRefreshToken(token: string) {
    this.googleRefreshToken = token;
    localStorage.setItem(LOCAL_STORAGE_KEYS.GOOGLE_REFRESH_TOKEN, token);
  }

  isValid = (token: string): boolean => {
    const claim = parseJWTToken(token);
    if (!claim) {
      return false;
    }
    const currentSeconds = Math.floor(new Date().valueOf() / 1000);
    return currentSeconds < claim.exp;
  };

  private saveUserMetaData(tokenData: {}) {
    const data = filterKeys(tokenData, this.requiredDataKeys);
    this.userMetaData = data;
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.USER_META_DATA,
      JSON.stringify(data)
    );
  }

  saveGoogleCodes = (res: {
    access_token: string;
    refresh_token: string;
    code: string;
  }) => {
    this.saveGoogleAccessToken(res?.access_token);
    this.saveGoogleRefreshToken(res?.refresh_token);
    this.saveGoogleAuthToken(res?.code);
  };

  login = async (res: { access_token: string }) => {
    let tokenData = await fetchUserData(res?.access_token);
    this.saveUserMetaData(tokenData);
    this.saveAccessToken(tokenData?.access_token);
    this.saveRefreshToken(tokenData?.refresh_token);
    this.authenticated = this.isValid(tokenData.access_token);
    return { isAuthenticated: this.authenticated, userDetails: tokenData };
  };

  private removeKeyFromLocalStorage = (key: string) => {
    localStorage.removeItem(key);
  };

  private emptyLocalStorage = () => {
    const filteredKeys = Object.keys(LOCAL_STORAGE_KEYS).filter((key) =>
      REMOVE_TOKENS.LOCAL_STORAGE_KEYS.includes(key)
    );
    filteredKeys.forEach((key) => {
      this.removeKeyFromLocalStorage(key);
    });
  };

  private resetParams = () => {
    this.accessToken = null;
    this.refreshToken = null;
    // this.googleAccessToken = null;
    // this.googleRefreshToken = null;
    // this.googleAuthToken = null;
    this.authenticated = false;
    this.isRefreshing = false;
    this.refreshSubscribers = [];
  };

  discardSession() {
    googleLogout();
    this.emptyLocalStorage();
    this.resetParams();
  }
}
